import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector, useStore } from "react-redux";
import * as Sentry from "@sentry/react";

import commonReducer, { UPDATE_USER } from "./common";
import tradeReducer, {
    updatePairedTokensCache,
    updateTradeActionTypeCache,
    updateTradeToken,
} from "./trade";
import tokenReducer from "./token";

import { doLogin } from "~/api/http";
import localStorage from "~/tools/localStorage";

const rootReducer = combineReducers({
    common: commonReducer,
    trade: tradeReducer,
    token: tokenReducer,
});

export type RootState = ReturnType<typeof rootReducer>;

const sentryReduxEnhancer = Sentry.createReduxEnhancer();

const initialStore = (preloadedState: Partial<RootState>) =>
    configureStore({
        devTools: process.env.NODE_ENV !== "production",
        reducer: rootReducer,
        preloadedState,
        enhancers: (getDefaultEnhancers) => {
            return getDefaultEnhancers().concat(sentryReduxEnhancer);
        },
    });

export type AppStore = ReturnType<typeof initialStore>;
export type AppDispatch = AppStore["dispatch"];

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();
export const useAppStore = useStore.withTypes<AppStore>();

export * from "./common";

// 初始化--------
export const initStoreCommon = async (store: AppStore) => {
    const p1 = localStorage.get("__uxuy_dex_cached_trade_token__");
    const p2 = localStorage.get("__uxuy_dex_cached_paired_tokens__");
    const p3 = localStorage.get("__uxuy_dex_cached_action_types__");
    const [user, tokenInfo, uTokensInfo, actionTypesInfo] = await Promise.all([
        doLogin(),
        p1,
        p2,
        p3,
    ]);

    if (!user) {
        throw new Error("Login Failed!");
    }

    store.dispatch(UPDATE_USER(user));
    store.dispatch(updateTradeToken(tokenInfo));
    store.dispatch(updatePairedTokensCache(uTokensInfo));
    store.dispatch(updateTradeActionTypeCache(actionTypesInfo));
};

export default initialStore;
